/**
 * @file CalloutNode.ts
 * @description Кастомная Tiptap-нода Callout (phase12.0).
 *
 * Цветной блок-«важно»/«совет»/«предупреждение» — для выделения ключевых
 * мыслей в статье. Содержит inline-контент (параграфы, текст).
 *
 * HTML-выход: `<div data-type="callout" data-variant="info|tip|warning">…</div>`.
 * Стилизация — в `apps/admin/src/index.css` и `apps/web/src/app/globals.css`
 * через селекторы `.article-body div[data-type="callout"]`.
 */

import { Node, mergeAttributes } from "@tiptap/core";

export type CalloutVariant = "info" | "tip" | "warning";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    callout: {
      /** Вставить блок Callout с указанным вариантом и пустым содержимым. */
      insertCallout: (variant: CalloutVariant) => ReturnType;
      /** Изменить вариант текущего Callout под курсором. */
      setCalloutVariant: (variant: CalloutVariant) => ReturnType;
    };
  }
}

export const CalloutNode = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: "info" as CalloutVariant,
        parseHTML: (el) =>
          (el.getAttribute("data-variant") as CalloutVariant) ?? "info",
        renderHTML: (attrs) => ({ "data-variant": attrs.variant }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="callout"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes({ "data-type": "callout" }, HTMLAttributes),
      0,
    ];
  },

  addCommands() {
    return {
      insertCallout:
        (variant) =>
        ({ chain }) =>
          chain()
            .focus()
            .insertContent({
              type: this.name,
              attrs: { variant },
              content: [{ type: "paragraph" }],
            })
            .run(),
      setCalloutVariant:
        (variant) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { variant }),
    };
  },
});
